// @flow
import React, { memo } from 'react';
import type { Node } from 'react';
import {
  Banner,
  Button,
  Divider,
  Skeleton,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  ArrowBackIcon,
  ExpandMoreIcon,
  DownloadIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Link } from 'react-router-dom';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import {
  DEFAULT_TRACS_VOUCHER_ID,
  VOUCHER_SUBMISSION_STATUS,
} from '../../constants/hudVoucher';
import {
  BADGE_STATUS_MESSAGE_MAP,
  getHUDVoucherBadgeConfig,
} from '../../constants/badgeStatus';

interface VoucherDetailsHeaderProps {
  // TODO: Change/map to the actual voucher type that the backend returns
  propertyId: string;
  isVoucherLoading?: boolean;
  voucher: {
    id: string,
    propertyId: string,
    unitId: string,
    residentId: string,
    caHudApprovedAmount?: number | string,
    voucherType: string,
    startDate: string,
    endDate: string,
    voucherYearMonth: string,
    // Record status (Active/Inactive/Pending)
    status: string,
    createdAt: string,
    updatedAt: string,
    deletedAt?: string,
    // These are the types I'm not sure about yet
    tracsId: string,
    // Submission status (Draft/Submitted/Error/Paid)
    submissionStatus?: string,
    month?: string,
    year?: string,
    requested: string,
    contractNumber: string,
    subsidyType?: string,
    tracsVoucherId?: string,
    hasDraftChangesSinceSubmission?: boolean,
  };
  showSubmittedChangesWarning?: boolean;
  onUndoSubmittedChanges?: () => void;
  isUndoingSubmittedChanges?: boolean;
  onDownloadHud52670?: () => void;
  isDownloading52670?: boolean;
  voucherActionSlot?: Node;
  showVarianceWarning?: boolean;
}

type TitleItemProps = {
  label: string,
  value: string,
};

type ListItemProps = {
  label: string,
  value?: Node,
  badge?: Node,
};

const TitleItem = ({ label, value }: TitleItemProps) => {
  return (
    <Stack>
      <Typography variant="bodyLarge">{label}</Typography>
      <Typography variant="titleMedium">{value}</Typography>
    </Stack>
  );
};

const ListItem = ({ label, value, badge }: ListItemProps) => {
  return (
    <Stack direction="row" gap={1} alignItems="center">
      <Typography variant="labelMedium">{label}</Typography>
      {value ? (
        <Typography
          variant="bodySmall"
          component="div"
          sx={{
            minHeight: 24,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {value}
        </Typography>
      ) : null}
      {badge}
    </Stack>
  );
};

const NUMBER_FORMAT_OPTIONS = { minimumFractionDigits: 2 };

function VoucherDetailsHeaderInner({
  voucher,
  propertyId,
  showSubmittedChangesWarning,
  onUndoSubmittedChanges,
  isUndoingSubmittedChanges,
  onDownloadHud52670,
  isDownloading52670,
  isVoucherLoading = false,
  voucherActionSlot,
  showVarianceWarning = false,
}: VoucherDetailsHeaderProps) {
  const intl = useIntl();
  const displayTracsVoucherId =
    voucher.tracsVoucherId ||
    voucher.tracsId ||
    (!isVoucherLoading && voucher.submissionStatus === 'Draft'
      ? DEFAULT_TRACS_VOUCHER_ID
      : '');
  const displaySubsidyType =
    voucher.subsidyType ??
    voucher.voucherType ??
    intl.formatMessage(messages.notAvailable);
  const rawSubmissionStatus = voucher.submissionStatus;
  const submissionStatus =
    rawSubmissionStatus ?? intl.formatMessage(messages.notAvailable);
  const submissionStatusMessageKey =
    BADGE_STATUS_MESSAGE_MAP[rawSubmissionStatus];
  const displaySubmissionStatus = submissionStatusMessageKey
    ? intl.formatMessage(messages[submissionStatusMessageKey])
    : submissionStatus;

  const shouldShowRequestedTotal = ['Submitted', 'Approved', 'Paid'].includes(
    rawSubmissionStatus ?? '',
  );
  const requestedValue = shouldShowRequestedTotal
    ? formatCurrency(voucher.requested, NUMBER_FORMAT_OPTIONS)
    : intl.formatMessage(messages.notAvailable);

  const shouldShowPaidTotal =
    rawSubmissionStatus === VOUCHER_SUBMISSION_STATUS.PAID &&
    voucher.caHudApprovedAmount != null;
  const paidValue = shouldShowPaidTotal
    ? formatCurrency(voucher.caHudApprovedAmount, NUMBER_FORMAT_OPTIONS)
    : intl.formatMessage(messages.notAvailable);

  return (
    <div>
      <Link to={`/property/${propertyId}/manage-hud-vouchers`}>
        <Button
          startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(90deg)' }} />}
          variant="text"
        >
          {intl.formatMessage(messages.goBackToManageHudVouchers)}
        </Button>
      </Link>
      <Stack gap={1}>
        <Banner
          open={showSubmittedChangesWarning}
          status="warning"
          ButtonProps={{
            startIcon: <ArrowBackIcon fontSize="small" />,
            disabled: !onUndoSubmittedChanges || isUndoingSubmittedChanges,
            onClick: onUndoSubmittedChanges,
            children: intl.formatMessage(messages.undoSubmittedChanges),
          }}
          title={intl.formatMessage(messages.submittedChangesWarningTitle)}
          message={intl.formatMessage(messages.submittedChangesWarningBody)}
        />
        <Banner
          open={showVarianceWarning}
          status="error"
          message={intl.formatMessage(messages.varianceWarningMessage)}
        />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="headlineMedium">
            {intl.formatMessage(messages.voucherDetailsTitle, {
              tracsVoucherId: displayTracsVoucherId,
            })}
          </Typography>

          {voucherActionSlot ?? null}
        </Stack>

        <Stack direction="row" gap={2}>
          <TitleItem
            label={intl.formatMessage(messages.requested)}
            value={requestedValue}
          />
          <Divider orientation="vertical" flexItem />
          <TitleItem
            label={intl.formatMessage(messages.paid)}
            value={paidValue}
          />
          <Divider orientation="vertical" flexItem />

          <Stack spacing={1} justifyContent={'center'} sx={{ marginLeft: 2 }}>
            <ListItem
              label={intl.formatMessage(messages.subsidyType)}
              value={displaySubsidyType}
            />
            <ListItem
              label={intl.formatMessage(messages.contractNumber)}
              value={
                voucher.contractNumber ||
                intl.formatMessage(messages.notAvailable)
              }
            />
          </Stack>
          <Stack spacing={1} justifyContent={'center'}>
            <ListItem
              label={intl.formatMessage(messages.voucherYearMonth)}
              value={voucher.voucherYearMonth}
            />
            <ListItem
              label={intl.formatMessage(messages.voucherStatus)}
              badge={(() => {
                if (isVoucherLoading) {
                  return <Skeleton variant="rounded" width={72} height={24} />;
                }
                const badgeConfig =
                  getHUDVoucherBadgeConfig(rawSubmissionStatus);
                return (
                  <BadgeStatus
                    status={badgeConfig.status}
                    variant={badgeConfig.variant}
                    sx={badgeConfig.sx}
                  >
                    {displaySubmissionStatus}
                  </BadgeStatus>
                );
              })()}
            />
          </Stack>
        </Stack>

        <Button
          size="small"
          variant="primarySubtle"
          startIcon={<DownloadIcon />}
          sx={{ maxWidth: 'fit-content' }}
          onClick={onDownloadHud52670}
          disabled={isDownloading52670}
        >
          {intl.formatMessage(messages.download52670)}
        </Button>
      </Stack>
    </div>
  );
}

const areEqual = (
  prev: VoucherDetailsHeaderProps,
  next: VoucherDetailsHeaderProps,
): boolean =>
  prev.voucher === next.voucher &&
  prev.propertyId === next.propertyId &&
  prev.showSubmittedChangesWarning === next.showSubmittedChangesWarning &&
  prev.onUndoSubmittedChanges === next.onUndoSubmittedChanges &&
  prev.isUndoingSubmittedChanges === next.isUndoingSubmittedChanges &&
  prev.onDownloadHud52670 === next.onDownloadHud52670 &&
  prev.isDownloading52670 === next.isDownloading52670 &&
  prev.isVoucherLoading === next.isVoucherLoading &&
  prev.showVarianceWarning === next.showVarianceWarning &&
  Boolean(prev.voucherActionSlot) === Boolean(next.voucherActionSlot);

const VoucherDetailsHeader: React$AbstractComponent<
  VoucherDetailsHeaderProps,
  mixed,
> = memo(VoucherDetailsHeaderInner, areEqual);

export default VoucherDetailsHeader;
